import TextInput from 'components/Input/TextInput';
import TextArea from 'components/Input/Textarea';
import Modal from 'components/Modal';
import { Formik } from 'formik';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import service from 'services';
import actions from 'state/actions';
import selectors from 'state/selectors';
import styled from 'styled-components/macro';
import appHelpers from 'utils/helpers/app.helpers';
import IntlHelper from 'utils/helpers/intl.helper';

const Form = styled.div`
  input {
    margin-bottom: 20px;
  }
`;

interface Values {
  toadr: string;
  cc: string;
  ccn: string;
  oggetto: string;
  corpo: string;
}

interface Errors {
  toadr?: string;
  cc?: string;
  ccn?: string;
  oggetto?: string;
  corpo?: string;
}

const Mail = () => {
  const protocols = useSelector(selectors.documents.selectAllActiveDocumentsID);
  const idUser = useSelector(selectors.user.getIdUser);
  const programCode = useSelector(selectors.app.getProgramCode);
  const isArchived = useSelector(selectors.documents.selectIsTemplateArchived);
  const dispatch = useDispatch();
  const t = IntlHelper.useTranslator();

  const onSubmit = async (values: Values) => {
    if (idUser && protocols) {
      try {
        if (isArchived) {
          const { data } = await service.sendArchivedMail({
            protocols,
            idUser,
            idProgram: programCode,
            to: values.toadr,
            cc: values.cc,
            bcc: values.ccn,
            subject: values.oggetto,
            body: values.corpo,
          });
          appHelpers.notify('success', t(data));
        } else {
          const { data } = await service.sendMail({
            protocols,
            idUser,
            idProgram: programCode,
            to: values.toadr,
            cc: values.cc,
            bcc: values.ccn,
            subject: values.oggetto,
            body: values.corpo,
          });
          appHelpers.notify('success', t(data));
        }
      } catch (e) {
        console.error(e);
      }
    }
    dispatch(actions.modal.closeModal());
  };

  const validate = (values: Values) => {
    const errors: Errors = {};

    if (!values.toadr) {
      errors.toadr = 'Field Required';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.toadr)) {
      errors.toadr = 'Invalid email address';
    }

    if (values.cc && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.cc)) {
      errors.cc = 'Invalid email address';
    }

    if (values.ccn && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.ccn)) {
      errors.ccn = 'Invalid email address';
    }

    return errors;
  };

  return (
    <>
      <Formik
        onSubmit={(values) => onSubmit(values)}
        validate={validate}
        initialValues={{
          toadr: '',
          cc: '',
          ccn: '',
          oggetto: '',
          corpo: '',
        }}
      >
        {({ values, handleChange, errors, touched, submitForm }) => (
          <>
            <Modal.Header title={t('sendDocumentMailTitle')} subtitle={t('send-mail-description')} />
            <Modal.Content>
              <Form>
                <TextInput
                  id="send-mail-toadr"
                  fullWidth
                  onChange={handleChange}
                  placeholder={t('Recipient')}
                  value={values.toadr}
                  hasFeedback={errors && errors.toadr ? touched.toadr && errors?.toadr?.length > 0 : false}
                  feedbackMessage={errors.toadr}
                  name="toadr"
                />
                <TextInput
                  id="send-mail-cc"
                  fullWidth
                  onChange={handleChange}
                  placeholder={t('Cc')}
                  value={values.cc}
                  hasFeedback={errors && errors.cc ? touched.cc && errors.cc?.length > 0 : false}
                  feedbackMessage={errors.cc}
                  name="cc"
                />
                <TextInput
                  id="send-mail-ccn"
                  fullWidth
                  onChange={handleChange}
                  placeholder={t('Ccn')}
                  value={values.ccn}
                  hasFeedback={errors && errors.ccn ? touched.ccn && errors.ccn?.length > 0 : false}
                  feedbackMessage={errors.ccn}
                  name="ccn"
                />
                <TextInput
                  id="send-mail-oggetto"
                  fullWidth
                  onChange={handleChange}
                  placeholder={t('Subject')}
                  value={values.oggetto}
                  name="oggetto"
                />
                <TextArea name="corpo" placeholder={t('Body')} onChange={handleChange} value={values.corpo} />
              </Form>
            </Modal.Content>
            <Modal.Footer dontCloseOnConfirm confirmText={t('send-mail')} confirmAction={submitForm} />
          </>
        )}
      </Formik>
    </>
  );
};

export default Mail;
